import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Col,
    Checkbox,
    Collapse,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Tooltip,
    message
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { officeService } from '../../services/officeService';
import countries from '../../constants/countries';
import {
    buildLeavePolicyRuleAttributes,
    findLatestPolicyRuleForLeaveType,
    getLeavePolicyRuleDefaults,
    isPolicyVersionSelectableLeaveType,
    normalizePolicyCodeList
} from './leavePolicyHelpers';
import logger from '../../utils/logger';
import './LeavePolicyVersionModal.scss';

const { TextArea } = Input;
const DATE_FORMAT = 'DD MMM YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';
const GENDER_ELIGIBILITY_OPTIONS = [
    { label: 'Any gender', value: '' },
    { label: 'Male only', value: 'form.gender.male' },
    { label: 'Female only', value: 'form.gender.female' }
];
const NATIONALITY_OPTIONS = countries.map((country) => ({
    label: `${country.code} - ${country.name}`,
    value: country.code
}));
const JOINERS_UNTIL_TOOLTIP = 'Optional. Leave blank to apply this policy to all employees joining on or after the start date.';
const joinersUntilLabel = (
    <span>
        Joiners until{' '}
        <Tooltip title={JOINERS_UNTIL_TOOLTIP}>
            <InfoCircleOutlined
                aria-label="Joiners until information"
                tabIndex={0}
                style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
        </Tooltip>
    </span>
);

const getPolicySubjectLabel = (leaveType) => {
    const label = String(leaveType?.policy_label || leaveType?.leave_name || '').trim();
    if (!label) return 'Leave';
    return /\bleave$/i.test(label) ? label : `${label} Leave`;
};

const getJoinerEndValidation = (getFieldValue) => [{
    validator: (_, value) => {
        const joinerFrom = getFieldValue('joiner_from');
        if (!value || !joinerFrom || !value.isBefore(joinerFrom, 'day')) return Promise.resolve();
        return Promise.reject(new Error('Joiner end date cannot be earlier than the start date'));
    }
}];

const LeavePolicyVersionModal = ({
    visible,
    officeId,
    leaveTypes = [],
    policyVersions = [],
    initialLeaveTypeId = null,
    onClose,
    onSaved
}) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const generatedVersionCodeRef = useRef(null);
    const effectiveDateIsSyncedRef = useRef(true);
    const selectableLeaveTypes = useMemo(
        () => leaveTypes.filter(isPolicyVersionSelectableLeaveType).sort((left, right) => (
            String(left.policy_label || left.leave_name).localeCompare(String(right.policy_label || right.leave_name))
        )),
        [leaveTypes]
    );
    const initialLeaveType = useMemo(
        () => selectableLeaveTypes.find(
            (leaveType) => leaveType.id === Number(initialLeaveTypeId)
        ) || null,
        [initialLeaveTypeId, selectableLeaveTypes]
    );
    const policySubjectLabel = getPolicySubjectLabel(initialLeaveType);
    const policySubjectCode = String(initialLeaveType?.system_key || initialLeaveType?.leave_name || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const modalTitle = initialLeaveType
        ? `Change ${policySubjectLabel} Policy`
        : 'Create Leave Policy Version';
    const modalSubtitle = initialLeaveType
        ? 'This publishes a new historical version. Existing entitlements remain unchanged.'
        : 'Only add leave types changed by this revision';

    useEffect(() => {
        if (!visible) return;

        generatedVersionCodeRef.current = null;
        effectiveDateIsSyncedRef.current = true;

        form.setFieldsValue({
            version_code: undefined,
            effective_from: null,
            joiner_from: null,
            joiner_until: null,
            source_reference: undefined,
            policy_note: undefined,
            rules: [initialLeaveType
                ? getLeavePolicyRuleDefaults(
                    initialLeaveType,
                    findLatestPolicyRuleForLeaveType(policyVersions, initialLeaveType.id)
                )
                : { is_enabled: true }]
        });
    }, [visible, initialLeaveType, policyVersions, form]);

    const handleJoinerFromChange = (value) => {
        const currentVersionCode = form.getFieldValue('version_code');
        const currentEffectiveDate = form.getFieldValue('effective_from');
        const generatedVersionCode = value
            ? `new-joiners-${value.format(API_DATE_FORMAT)}${policySubjectCode ? `-${policySubjectCode}` : ''}`
            : undefined;
        const updates = {};

        if (!currentVersionCode || currentVersionCode === generatedVersionCodeRef.current) {
            updates.version_code = generatedVersionCode;
            generatedVersionCodeRef.current = generatedVersionCode || null;
        }

        if (!currentEffectiveDate || effectiveDateIsSyncedRef.current) {
            updates.effective_from = value || null;
            effectiveDateIsSyncedRef.current = true;
        }

        if (Object.keys(updates).length > 0) form.setFieldsValue(updates);
    };

    const handleLeaveTypeChange = (ruleIndex, leaveTypeId) => {
        const leaveType = selectableLeaveTypes.find((item) => item.id === Number(leaveTypeId));
        if (!leaveType) return;

        const rules = [...(form.getFieldValue('rules') || [])];
        rules[ruleIndex] = getLeavePolicyRuleDefaults(
            leaveType,
            findLatestPolicyRuleForLeaveType(policyVersions, leaveType.id)
        );
        form.setFieldsValue({ rules });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const ruleValues = values.rules || [];
            const leaveTypeIds = ruleValues.map((rule) => Number(rule.leave_type_id));
            if (new Set(leaveTypeIds).size !== leaveTypeIds.length) {
                message.error('Each leave type can only be added once');
                return;
            }

            const conflictingEligibilityRule = ruleValues.find((rule) => (
                normalizePolicyCodeList(rule.eligibility_nationality).length > 0
                && normalizePolicyCodeList(rule.eligibility_nationality_exclude).length > 0
            ));
            if (conflictingEligibilityRule) {
                message.error('Use either allowed nationalities or excluded nationalities, not both.');
                return;
            }

            const rules = ruleValues.map((rule) => {
                const leaveType = selectableLeaveTypes.find((item) => item.id === Number(rule.leave_type_id));
                if (!leaveType) throw new Error('Selected leave type is no longer available');

                return {
                    leave_type_id: leaveType.id,
                    leave_name: leaveType.leave_name,
                    is_enabled: rule.is_enabled !== false,
                    days: rule.is_enabled === false ? null : rule.days,
                    description: rule.description?.trim() || null,
                    attributes: buildLeavePolicyRuleAttributes(leaveType, rule)
                };
            });

            setSaving(true);
            const result = await officeService.publishLeavePolicyVersion(officeId, {
                version_code: values.version_code.trim(),
                effective_from: values.effective_from.format(API_DATE_FORMAT),
                joiner_from: values.joiner_from.format(API_DATE_FORMAT),
                joiner_until: values.joiner_until?.format(API_DATE_FORMAT) || null,
                source_reference: values.source_reference?.trim() || null,
                policy_note: values.policy_note?.trim() || null,
                rules
            });
            message.success('Leave policy version published');
            onSaved?.(result);
        } catch (error) {
            if (error?.errorFields) return;
            logger.error('Error publishing leave policy version:', error);
            message.error(error.response?.data?.message || error.message || 'Failed to publish leave policy version');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            visible={visible}
            onClose={() => onClose?.()}
            width="medium"
            className="leave-policy-version-modal"
        >
            <Modal.Header title={modalTitle} subtitle={modalSubtitle} />
            <Modal.Body padding="default">
                <Form form={form} layout="vertical" className="leave-policy-version-form">
                    <section className="leave-policy-form-section" aria-labelledby="policy-version-details-heading">
                        <h3 id="policy-version-details-heading" className="leave-policy-form-section__title">
                            Version details
                        </h3>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Version code"
                                    name="version_code"
                                    rules={[
                                        { required: true, message: 'Please enter a version code' },
                                        { max: 128, message: 'Maximum 128 characters' }
                                    ]}
                                >
                                    <Input
                                        placeholder="Generated from joiner date"
                                        maxLength={128}
                                        onChange={() => {
                                            generatedVersionCodeRef.current = null;
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Source reference" name="source_reference" rules={[{ max: 255 }]}>
                                    <Input placeholder="Policy document or email" maxLength={255} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Joiners from" name="joiner_from" rules={[{ required: true, message: 'Please select the joiner start date' }]}>
                                    <DateField allowClear placeholder="Select date" onChange={handleJoinerFromChange} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label={joinersUntilLabel} name="joiner_until" rules={getJoinerEndValidation(form.getFieldValue)}>
                                    <DateField allowClear placeholder="No end date" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Effective from"
                                    name="effective_from"
                                    rules={[{ required: true, message: 'Please select the effective date' }]}
                                >
                                    <DateField
                                        allowClear
                                        placeholder="Follows joiner date"
                                        onChange={() => {
                                            effectiveDateIsSyncedRef.current = false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </section>

                    <section className="leave-policy-form-section" aria-labelledby="policy-changes-heading">
                        <h3 id="policy-changes-heading" className="leave-policy-form-section__title">
                            Changed leave policy
                        </h3>
                        <Form.List
                            name="rules"
                            rules={[{
                                validator: async (_, rules) => {
                                    if (!rules || rules.length === 0) throw new Error('Add at least one leave entitlement');
                                }
                            }]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <div key={key} className="leave-policy-rule">
                                            <div className="leave-policy-rule__header">
                                                <Form.Item noStyle shouldUpdate>
                                                    {() => {
                                                        const selectedLeaveTypeId = form.getFieldValue([
                                                            'rules',
                                                            name,
                                                            'leave_type_id'
                                                        ]);
                                                        const selectedLeaveType = selectableLeaveTypes.find(
                                                            (leaveType) => leaveType.id === Number(selectedLeaveTypeId)
                                                        );

                                                        return (
                                                            <span>
                                                                {selectedLeaveType
                                                                    ? selectedLeaveType.policy_label || selectedLeaveType.leave_name
                                                                    : 'Select leave type'}
                                                            </span>
                                                        );
                                                    }}
                                                </Form.Item>
                                                {fields.length > 1 && (
                                                    <Button
                                                        {...BUTTON_PRESETS.DELETE}
                                                        type="button"
                                                        uppercase={false}
                                                        size="small"
                                                        onClick={() => remove(name)}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>

                                            <Row gutter={[16, 0]}>
                                                <Col xs={24} md={16}>
                                                    {initialLeaveType ? (
                                                        <>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'leave_type_id']}
                                                                hidden
                                                            >
                                                                <input type="hidden" />
                                                            </Form.Item>
                                                            <Form.Item label="Leave type">
                                                                <Input
                                                                    aria-label="Leave type"
                                                                    value={initialLeaveType.policy_label || initialLeaveType.leave_name}
                                                                    readOnly
                                                                />
                                                            </Form.Item>
                                                        </>
                                                    ) : (
                                                        <Form.Item
                                                            {...restField}
                                                            label="Leave type"
                                                            name={[name, 'leave_type_id']}
                                                            rules={[{ required: true, message: 'Select a leave type' }]}
                                                        >
                                                            <Select
                                                                placeholder="Select leave type"
                                                                onChange={(leaveTypeId) => handleLeaveTypeChange(name, leaveTypeId)}
                                                                options={selectableLeaveTypes.map((leaveType) => ({
                                                                    value: leaveType.id,
                                                                    label: leaveType.policy_label || leaveType.leave_name
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item
                                                        {...restField}
                                                        label="Days"
                                                        name={[name, 'days']}
                                                        rules={[{
                                                            validator: (_, value) => {
                                                                const enabled = form.getFieldValue(['rules', name, 'is_enabled']) !== false;
                                                                return !enabled || value !== null && value !== undefined
                                                                    ? Promise.resolve()
                                                                    : Promise.reject(new Error('Enter entitlement days'));
                                                            }
                                                        }]}
                                                    >
                                                        <InputNumber min={0} step={0.5} precision={1} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <div className="leave-policy-rule__options">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'is_enabled']}
                                                    valuePropName="checked"
                                                    noStyle
                                                >
                                                    <Checkbox>Available for affected employees</Checkbox>
                                                </Form.Item>
                                            </div>

                                            <Form.Item noStyle shouldUpdate>
                                                {() => form.getFieldValue(['rules', name, 'leave_type_id']) ? (
                                                    <Collapse
                                                        ghost
                                                        size="small"
                                                        className="leave-policy-rule__advanced"
                                                        items={[{
                                                            key: 'eligibility-description',
                                                            label: 'Eligibility and description',
                                                            children: (
                                                                <div className="leave-policy-rule__details">
                                                                    <Row gutter={[16, 0]}>
                                                                        <Col xs={24} md={8}>
                                                                            <Form.Item {...restField} label="Eligible gender" name={[name, 'eligibility_gender']}>
                                                                                <Select options={GENDER_ELIGIBILITY_OPTIONS} />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={8}>
                                                                            <Form.Item {...restField} label="Allowed nationalities" name={[name, 'eligibility_nationality']}>
                                                                                <Select
                                                                                    mode="multiple"
                                                                                    allowClear
                                                                                    showSearch
                                                                                    optionFilterProp="label"
                                                                                    placeholder="All nationalities"
                                                                                    options={NATIONALITY_OPTIONS}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={8}>
                                                                            <Form.Item {...restField} label="Excluded nationalities" name={[name, 'eligibility_nationality_exclude']}>
                                                                                <Select
                                                                                    mode="multiple"
                                                                                    allowClear
                                                                                    showSearch
                                                                                    optionFilterProp="label"
                                                                                    placeholder="No exclusions"
                                                                                    options={NATIONALITY_OPTIONS}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        className="leave-policy-rule__description"
                                                                        label="Policy description"
                                                                        name={[name, 'description']}
                                                                        rules={[{ max: 255, message: 'Maximum 255 characters' }]}
                                                                    >
                                                                        <TextArea rows={2} maxLength={255} />
                                                                    </Form.Item>
                                                                </div>
                                                            )
                                                        }]}
                                                    />
                                                ) : null}
                                            </Form.Item>
                                        </div>
                                    ))}
                                    <Form.ErrorList errors={errors} />
                                    {!initialLeaveType && (
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            icon={BUTTON_PRESETS.ADD.icon}
                                            uppercase={false}
                                            size="small"
                                            onClick={() => add({ is_enabled: true })}
                                        >
                                            Add another leave type
                                        </Button>
                                    )}
                                </>
                            )}
                        </Form.List>
                    </section>

                    <section className="leave-policy-form-section" aria-labelledby="policy-note-heading">
                        <h3 id="policy-note-heading" className="leave-policy-form-section__title">Note</h3>
                        <Form.Item name="policy_note" rules={[{ max: 2000 }]} className="leave-policy-form-note">
                            <TextArea rows={2} maxLength={2000} />
                        </Form.Item>
                    </section>
                </Form>
            </Modal.Body>
            <Modal.Footer
                showDefaultClose={false}
                buttons={[
                    {
                        ...BUTTON_PRESETS.CANCEL,
                        key: 'cancel-policy-version',
                        label: 'Cancel',
                        disabled: saving,
                        onClick: () => onClose?.()
                    },
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: 'publish-policy-version',
                        label: initialLeaveType ? 'Publish Policy Change' : 'Publish version',
                        loading: saving,
                        onClick: handleSave
                    }
                ]}
            />
        </Modal>
    );
};

const DateField = ({ allowClear = false, ...props }) => (
    <DatePicker {...props} allowClear={allowClear} format={DATE_FORMAT} style={{ width: '100%' }} />
);

export default LeavePolicyVersionModal;
